import { AnimatePresence, motion } from "motion/react";
import { CircleCheck, CloudUpload, File, FileImage, FileMusic, FileVideo, LoaderCircle, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { montageApi, readVideoDuration } from "../api";
import { notify } from "../lib/notify";
import { captureFrame } from "../lib/poster";
import { spring } from "../lib/motion";
import { cn, formatBytes, formatDuration, uid } from "../lib/utils";
import type { FileKind } from "../types";

export interface UploadItem {
  id: string;
  name: string;
  size: number;
  kind: FileKind;
  progress: number;
  done: boolean;
  url?: string;
  /** секунды — для видео */
  duration?: number;
  /** путь в хранилище после загрузки — его называют серверу */
  path?: string;
  /** кадр-превью версии, если снялся */
  posterPath?: string;
}

export const kindOf = (name: string, mime = ""): FileKind => {
  if (mime.startsWith("video/") || /\.(mp4|mov|m4v|webm|mkv|avi)$/i.test(name)) return "video";
  if (mime.startsWith("audio/") || /\.(wav|mp3|m4a|aac|flac|ogg)$/i.test(name)) return "audio";
  if (mime.startsWith("image/") || /\.(png|jpe?g|gif|webp|heic|psd)$/i.test(name)) return "image";
  return "doc";
};

export const KindIcon = ({ kind, className }: { kind: FileKind; className?: string }) => {
  const C = kind === "video" ? FileVideo : kind === "audio" ? FileMusic : kind === "image" ? FileImage : File;
  return <C className={className} />;
};

type UploadTarget = { purpose: "source" } | { purpose: "version"; taskId: string };

/**
 * Загрузка в хранилище по подписанной ссылке, с настоящим прогрессом.
 *
 * Исходники постановщика ложатся в его черновой каталог ещё до постановки —
 * сервер потом пускает в задачу только их. Версия монтажёра ложится сразу в
 * каталог задачи, а рядом — кадр-превью для доски.
 */
export function useUploads(target: UploadTarget = { purpose: "source" }) {
  const [items, setItems] = useState<UploadItem[]>([]);
  const controllers = useRef<Record<string, AbortController>>({});
  const purpose = target.purpose;
  const taskId = target.purpose === "version" ? target.taskId : undefined;

  useEffect(() => {
    const c = controllers.current;
    return () => Object.values(c).forEach((ctrl) => ctrl.abort());
  }, []);

  const patch = useCallback((id: string, p: Partial<UploadItem>) => {
    setItems((l) => l.map((it) => (it.id === id ? { ...it, ...p } : it)));
  }, []);

  const addFiles = useCallback(
    (files: File[]) => {
      files.forEach((f) => {
        const kind = kindOf(f.name, f.type);
        const item: UploadItem = { id: uid(), name: f.name, size: f.size, kind, progress: 0, done: false };
        // Картинке — превью сразу: пост собирают из нескольких, их надо видеть.
        if (kind === "image") item.url = URL.createObjectURL(f);
        if (kind === "video") {
          item.url = URL.createObjectURL(f);
          void readVideoDuration(f).then((d) => {
            if (d) patch(item.id, { duration: d });
          });
        }
        setItems((l) => [...l, item]);

        const ctrl = new AbortController();
        controllers.current[item.id] = ctrl;
        void (async () => {
          try {
            const path = await montageApi.upload(purpose, f, {
              taskId,
              signal: ctrl.signal,
              // 100% — только когда файл принят: иначе «готово» загорится раньше, чем сервер его увидит
              onProgress: (p) => patch(item.id, { progress: Math.min(0.99, p / 100) }),
            });
            let posterPath: string | undefined;
            if (purpose === "version" && kind === "video") {
              const frame = await captureFrame(f);
              if (frame) {
                posterPath = await montageApi
                  .upload("poster", frame, { taskId, fileName: "poster.jpg" })
                  .catch(() => undefined);
              }
            }
            patch(item.id, { progress: 1, done: true, path, posterPath });
          } catch (error) {
            if ((error as Error).name === "AbortError") return;
            notify(`«${f.name}» не загрузился: ${(error as Error).message}`, { icon: "rework" });
            setItems((l) => l.filter((it) => it.id !== item.id));
          } finally {
            delete controllers.current[item.id];
          }
        })();
      });
    },
    [patch, purpose, taskId],
  );

  const remove = useCallback((id: string) => {
    controllers.current[id]?.abort();
    delete controllers.current[id];
    setItems((l) => l.filter((it) => it.id !== id));
  }, []);

  const reset = useCallback(() => setItems([]), []);

  return { items, addFiles, remove, reset, allDone: items.length > 0 && items.every((i) => i.done) };
}

export function Dropzone({
  onFiles,
  onDemo,
  demoLabel = "взять демо-файлы",
  title = "Перетащите исходники сюда или",
  hint = "до 4 ГБ",
  compact,
  multiple = true,
  accept,
  className,
}: {
  onFiles: (f: File[]) => void;
  onDemo?: () => void;
  demoLabel?: string;
  title?: string;
  hint?: string;
  compact?: boolean;
  multiple?: boolean;
  accept?: string;
  className?: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  return (
    <motion.div
      animate={{ scale: over ? 1.012 : 1 }}
      transition={spring.snappy}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const files = Array.from(e.dataTransfer.files);
        if (files.length) onFiles(multiple ? files : files.slice(0, 1));
      }}
      className={cn(
        "flex rounded-card border-[1.5px] border-dashed transition-colors",
        over ? "border-foreground/60 bg-muted" : "border-ring bg-sidebar",
        compact ? "flex-row items-center gap-3 px-4 py-3" : "flex-col items-center justify-center gap-3 px-5 py-6 text-center",
        className,
      )}
    >
      <input
        ref={input}
        type="file"
        hidden
        multiple={multiple}
        accept={accept}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length) onFiles(files);
          e.target.value = "";
        }}
      />
      <span className={cn("grid shrink-0 place-items-center rounded-full border bg-background shadow-mxs", compact ? "size-8" : "size-10")}>
        <CloudUpload className={compact ? "size-4" : "size-[18px]"} />
      </span>
      <div className={cn("flex min-w-0 flex-col gap-0.5", compact ? "items-start" : "items-center")}>
        <div className="text-sm font-medium">
          {title}{" "}
          <button type="button" onClick={() => input.current?.click()} className="underline underline-offset-2 hover:text-foreground/70">
            выберите файлы
          </button>
        </div>
        {!compact && hint && <div className="text-xs text-muted-foreground">{hint}</div>}
        {onDemo && (
          <button type="button" onClick={onDemo} className="text-xs font-medium text-muted-foreground underline-offset-2 hover:text-foreground hover:underline">
            или {demoLabel}
          </button>
        )}
      </div>
    </motion.div>
  );
}

export function UploadRow({ item, onRemove }: { item: UploadItem; onRemove?: () => void }) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={spring.smooth}
      className="overflow-hidden"
    >
      <motion.div
        animate={{ backgroundColor: item.done ? ["#f0fdf4", "#ffffff"] : "#ffffff" }}
        transition={{ duration: 0.9 }}
        className="flex flex-col gap-2 rounded-[20px] border px-3 py-2.5 pl-2.5"
      >
        <div className="flex items-center gap-3">
          <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-muted">
            <KindIcon kind={item.kind} className="size-4" />
          </span>
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-[13px] font-medium">{item.name}</span>
            <span className="text-xs text-muted-foreground tabular-nums">
              {formatBytes(item.size)}
              {item.duration ? ` · ${formatDuration(item.duration)}` : ""}
              {!item.done && ` · ${Math.round(item.progress * 100)}%`}
            </span>
          </div>
          <AnimatePresence mode="popLayout" initial={false}>
            {item.done ? (
              <motion.span key="ok" initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={spring.snappy}>
                <CircleCheck className="size-[18px] text-done" />
              </motion.span>
            ) : (
              <motion.span key="load" exit={{ scale: 0.6, opacity: 0 }}>
                <LoaderCircle className="size-[18px] animate-spin text-muted-foreground" />
              </motion.span>
            )}
          </AnimatePresence>
          {onRemove && (
            <button type="button" onClick={onRemove} className="grid size-7 place-items-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground">
              <X className="size-4" />
            </button>
          )}
        </div>
        <AnimatePresence initial={false}>
          {!item.done && (
            <motion.div exit={{ opacity: 0, height: 0 }} className="h-1 overflow-hidden rounded-full bg-muted">
              <motion.div
                className="shimmer h-full rounded-full bg-primary"
                animate={{ width: `${Math.round(item.progress * 100)}%` }}
                transition={{ ease: "linear", duration: 0.16 }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
}
